import { Avatar, FormCard } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import dayjs from 'dayjs'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AdministrationStatus, type StockRecord } from '../../types'
import {
  getStockChangedText,
  getUnitFromDoseText
} from '../../utils/administrationUtils'
import { RevisionHistorySection } from '../RevisionHistory/RevisionHistorySection'

const DEFAULT_TEST_ID = 'emar-adm-drawer'

type AdministrationRecordDetailContentProps = {
  record: StockRecord
  testId?: string
}

export const AdministrationRecordDetailContent = ({
  record,
  testId
}: AdministrationRecordDetailContentProps) => {
  const dataTestId = testId
    ? `${testId}-detail-content`
    : `${DEFAULT_TEST_ID}-detail-content`
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const adminData = record.administration
  if (!adminData) {
    return null
  }

  const isBackSigned =
    !!record.administeredBy ||
    !!record.backsignedAt ||
    !!(record as any).administeredById

  const customDose = adminData.medication?.customDose
  const dose = customDose || adminData.doseQuantity || adminData.dose

  const altDose = record.altDose
  const unit =
    record.unit ||
    record?.quantityType ||
    record?.administration?.quantityType ||
    record?.stock?.unit ||
    record?.administration?.stock?.unit ||
    ''

  // A free-text dose carries its own unit ("8 Puffs"), as does a dose already
  // formatted by the stock endpoints ("2 tablets"). Appending the resolved unit
  // to one of those doubled up, and with no dose unit defined the fallbacks
  // reach the *stock* unit — printing "8 Puffs box" (ROST-3829). Only a bare
  // number needs a unit added.
  //
  // `customDose` is free text whatever it looks like, so provenance decides it
  // rather than the shape of the text: a carer who typed "1.5" meant 1.5 of
  // whatever the label says, not 1.5 of the stock unit ("1.5 box").
  const doseNeedsUnit =
    !customDose && !(typeof dose === 'string' && !/^[\d.]+$/.test(dose.trim()))

  // "Was the medication used?" reports a dose rather than a stock movement, so
  // it needs the dose unit. A free-text dose is the only place that lives for a
  // medication with none configured ("8 Puffs"), and `unit` above falls through
  // to the stock unit, which is how the line read "8 box was used" (ROST-3829).
  // A custom dose with no unit in its text has none to show — not the box.
  const doseUnit = customDose ? (getUnitFromDoseText(customDose) ?? '') : unit

  // A medication with stock tracking off can have no unit anywhere in the
  // record, so the parts are joined rather than interpolated — an absent unit
  // used to print as a literal "undefined" beside the dose (ROST-3830).
  const doseText = altDose ? `${dose} or ${altDose}` : `${dose}`
  const administeredText =
    dose === undefined
      ? '-'
      : [doseText, doseNeedsUnit ? unit : ''].filter(Boolean).join(' ')

  const recordedBy = adminData.recordedBy
  const signedByName =
    typeof recordedBy.name === 'object'
      ? `${recordedBy.name.first} ${recordedBy.name.last}`
      : recordedBy.name || ''

  const timeLogged = isBackSigned
    ? dayjs(record.backsignedAt || adminData.updatedAt || record.date).format(
        'DD-MM-YYYY HH:mm'
      )
    : dayjs(record.date || adminData.administeredTime).format(
        'DD-MM-YYYY HH:mm'
      )

  const comment = adminData.note || record.notes || ''
  const commentText = Array.isArray(comment) ? comment.join(', ') : comment

  const isStockTrackingOff =
    adminData.medication?.hasStockTracking === false ||
    adminData?.administration?.stockAffected === false

  const stockChangedText = getStockChangedText({
    administration: adminData,
    unit,
    doseUnit,
    stockChanged: isStockTrackingOff ? false : record.stockChanged,
    quantityChange: record.quantityChange,
    stockAffected: isStockTrackingOff ? false : undefined,
    t
  })

  const adminBy = record.administeredBy
  const adminByName = adminBy
    ? typeof adminBy.name === 'object'
      ? `${adminBy.name.first} ${adminBy.name.last}`
      : adminBy.name || '-'
    : '-'

  return (
    <SectionContainer data-testid={dataTestId}>
      <FormCard level={2}>
        {/* Row 1: dose info — 3 cols for back-signed, 2 for regular */}
        <InfoRow>
          {isBackSigned && record.roundTime && (
            <InfoSection>
              <StyledLabel level={2}>{t('SCHEDULED_FOR')}</StyledLabel>
              <Text>{dayjs(record.roundTime).format('DD-MM-YYYY HH:mm')}</Text>
            </InfoSection>
          )}
          <InfoSection>
            <StyledLabel level={2}>
              {adminData.administrationStatus === AdministrationStatus.GIVEN
                ? t('HOW_MANY_ADMINISTERED')
                : t('WHAT_WAS_THE_DOSE_DUE')}
            </StyledLabel>
            <Text>{administeredText}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>
              {isStockTrackingOff
                ? t('WAS_MEDICATION_USED')
                : t('HAS_STOCK_AMOUNT_CHANGED')}
            </StyledLabel>
            <Text>{stockChangedText}</Text>
          </InfoSection>
        </InfoRow>

        <RowDivider />

        {/* Row 2: Comment */}
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('COMMENT')}</StyledLabel>
            <Text>{commentText || '-'}</Text>
          </FullWidthSection>
        </InfoRow>

        <RowDivider />

        {/* Row 3: Time recorded + witnesses (regular) or Corrected by (back-signed) */}
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          {!isBackSigned && record.witnesses && record.witnesses.length > 0 && (
            <InfoSection>
              <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
              <WitnessesContainer>
                {record.witnesses.map((witness, index) => {
                  const witnessName =
                    typeof witness.name === 'object'
                      ? `${witness.name.first} ${witness.name.last}`
                      : witness.name || '-'
                  return (
                    <WitnessItem key={witness.userId || index}>
                      <Avatar url={witness.picture ?? undefined} size={16} />
                      <Text weight="bold">{witnessName}</Text>
                    </WitnessItem>
                  )
                })}
              </WitnessesContainer>
            </InfoSection>
          )}
          <InfoSection>
            <StyledLabel level={2}>
              {isBackSigned ? t('CORRECTED_BY') : t('SIGNED_BY')}
            </StyledLabel>
            <SignedByContainer>
              <Avatar url={recordedBy.picture as unknown as string} size={16} />
              <Text weight="bold">{signedByName || '-'}</Text>
            </SignedByContainer>
          </InfoSection>
          {/* spacer keeps col 2 aligned with Row 4's 3-column layout */}
          {isBackSigned && <InfoSection />}
        </InfoRow>

        {/* Row 4: back-signed only — time offered, signed by (administeredBy), reason (no divider) */}
        {isBackSigned && (
          <InfoRow>
            <InfoSection>
              <StyledLabel level={2}>{t('TIME_OFFERED')}</StyledLabel>
              <Text>
                {record.actionTime
                  ? dayjs(record.actionTime).format('DD-MM-YYYY HH:mm')
                  : '-'}
              </Text>
            </InfoSection>
            <InfoSection>
              <StyledLabel level={2}>{t('SIGNED_BY')}</StyledLabel>
              <SignedByContainer>
                <Avatar url={adminBy?.picture as unknown as string} size={16} />
                <Text weight="bold">{adminByName}</Text>
              </SignedByContainer>
            </InfoSection>
            {record.reasonForLateSignature && (
              <InfoSection>
                <StyledLabel level={2}>
                  {t('REASON_FOR_LATE_SIGNATURE')}
                </StyledLabel>
                <Text>{record.reasonForLateSignature}</Text>
              </InfoSection>
            )}
          </InfoRow>
        )}
      </FormCard>
      {record.changelog && (
        <RevisionHistorySection changelog={record.changelog} isAdministration />
      )}
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const SignedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const WitnessesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WitnessItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
